import { Flex } from "@redotech/arbiter/flex";
import { DisabledTooltip } from "@redotech/arbiter/tooltip/disabled-tooltip";
import { useRequiredContext } from "@redotech/react-util/context";
import classNames from "classnames";
import { useEffect, useMemo, useRef, useState } from "react";
import * as styles from "./setting-section-tab-nav.module.css";
import { TabNavigationContext } from "./tab-navigation-provider";

const SMOOTH_SCROLL_FALLBACK_MS = 1000;

/** Nearest ancestor that actually scrolls, so we can listen for its scroll. */
function findScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

export function SettingSectionTabNav({
  rootMargin = "-200px 0px -50% 0px",
  activationOffset = 24,
}: {
  /** Band within the viewport that counts as "current". */
  rootMargin?: string;
  /** Distance from the scroll viewport's top that marks the current section. */
  activationOffset?: number;
} = {}) {
  const { sectionRefs, sectionErrors } =
    useRequiredContext(TabNavigationContext);
  const [activeSection, setActiveSection] = useState<string>("");
  // While a click-driven smooth scroll is in flight the scroll tracker would
  // pass through every section on the way, so it stays muted until it lands.
  const jumpTargetRef = useRef<string | null>(null);

  const eleList = useMemo(() => {
    return Object.entries(sectionRefs)
      .filter(([, { ref }]) => ref !== null)
      .sort(([, { ref: refA }], [, { ref: refB }]) => {
        if (!refA || !refB) {
          return 0;
        }
        const topA = refA.getBoundingClientRect().top + window.scrollY;
        const topB = refB.getBoundingClientRect().top + window.scrollY;
        return topA - topB;
      });
  }, [sectionRefs]);

  useEffect(() => {
    const enabledSections = Object.entries(sectionRefs).filter(
      ([, { disabledMessage, ref }]) => ref !== null && !disabledMessage,
    );
    const firstRef = enabledSections[0]?.[1].ref;
    const scroller = firstRef ? findScrollParent(firstRef) : null;

    // Track the last section above the activation line in the scroll owner.
    const computeActive = () => {
      if (jumpTargetRef.current) {
        return;
      }
      const currentScroller = firstRef ? findScrollParent(firstRef) : null;
      const line =
        (currentScroller ? currentScroller.getBoundingClientRect().top : 0) +
        activationOffset;
      const sorted = enabledSections.sort(([, a], [, b]) => {
        if (!a.ref || !b.ref) {
          return 0;
        }
        return (
          a.ref.getBoundingClientRect().top - b.ref.getBoundingClientRect().top
        );
      });
      const firstSection = sorted[0];
      if (!firstSection) {
        return;
      }
      let activeId = firstSection[0];
      for (const [id, { ref }] of sorted) {
        if (ref && ref.getBoundingClientRect().top <= line) {
          activeId = id;
        } else {
          break;
        }
      }
      // The last section may be too short to ever cross the line, so the
      // bottom of the scroller counts as reaching it.
      const scrollElement = currentScroller ?? document.scrollingElement;
      if (
        scrollElement &&
        scrollElement.scrollTop + scrollElement.clientHeight >=
          scrollElement.scrollHeight - 2
      ) {
        activeId = sorted.at(-1)?.[0] ?? activeId;
      }

      setActiveSection(activeId);
    };

    computeActive();

    const observer = new IntersectionObserver(computeActive, {
      threshold: [0, 1],
      rootMargin,
    });
    for (const [, { ref }] of enabledSections) {
      if (ref) {
        observer.observe(ref);
      }
    }

    scroller?.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("scroll", computeActive, {
      passive: true,
      capture: true,
    });

    return () => {
      observer.disconnect();
      scroller?.removeEventListener("scroll", computeActive);
      window.removeEventListener("scroll", computeActive, { capture: true });
    };
  }, [sectionRefs, rootMargin, activationOffset]);

  const jumpTo = (id: string, elementRef: HTMLElement | null) => {
    if (!elementRef) {
      return;
    }
    jumpTargetRef.current = id;
    setActiveSection(id);
    elementRef.scrollIntoView({ behavior: "smooth", block: "start" });
    const release = () => {
      jumpTargetRef.current = null;
      document.removeEventListener("scrollend", release, true);
      window.clearTimeout(fallback);
    };
    const fallback = window.setTimeout(release, SMOOTH_SCROLL_FALLBACK_MS);
    document.addEventListener("scrollend", release, {
      capture: true,
      once: true,
    });
  };

  return (
    <div className={styles.sideNavContainer}>
      <Flex dir="column" gap="none" w="full">
        {eleList.map(([id, { disabledMessage, title, ref }]) => (
          <SettingSectionTabNavItem
            active={activeSection === id}
            disabledMessage={disabledMessage}
            hasError={(sectionErrors[id]?.length ?? 0) > 0}
            key={id}
            onSelect={() => jumpTo(id, ref)}
            title={title}
          />
        ))}
      </Flex>
    </div>
  );
}

function SettingSectionTabNavItem({
  title,
  active,
  disabledMessage,
  hasError,
  onSelect,
}: {
  title: string;
  active: boolean;
  disabledMessage?: string;
  hasError: boolean;
  onSelect: () => void;
}) {
  return (
    <DisabledTooltip
      disabledMessage={disabledMessage}
      otherTooltipProps={{ placement: "right" }}
      wrapperClass={styles.sideNavTooltip}
    >
      <Flex
        align="center"
        as="button"
        className={classNames(
          styles.sideNavLink,
          active && styles.active,
          disabledMessage && styles.disabled,
        )}
        disabled={!!disabledMessage}
        gap="xs"
        onClick={disabledMessage ? undefined : onSelect}
        type="button"
      >
        {title}
        {hasError && <span className={styles.errorDot} />}
      </Flex>
    </DisabledTooltip>
  );
}
